import React, { useState, useEffect, useCallback, useContext } from "react";
import axios from "axios";
import { debounce } from "lodash";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import AuthContext from "../context/AuthContext";

const API_URL = process.env.REACT_APP_API_URL;

const TeamList = () => {
  const { user } = useContext(AuthContext);
  const [teams, setTeams] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const limit = 10;

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTeam, setCurrentTeam] = useState({
    id: null,
    name: "",
    club: "",
    stadium: "",
    status: 1,
    level_team_id: "",
  });
  const [teamLevels, setTeamLevels] = useState([]);

  const fetchTeams = useCallback(
    debounce(async (currentPage, currentSearch, currentStatus) => {
      if (!user) return;
      try {
        if (user.role === "manager") {
          const teamRes = await axios.get(`${API_URL}/teams/${user.teamId}`);
          setTeams(teamRes.data ? [teamRes.data] : []);
          setTotalPages(1);
        } else {
          const params = {
            page: currentPage,
            limit,
            searchTerm: currentSearch,
            include_inactive: 1,
            ...(currentStatus !== "all" ? { status: currentStatus } : {}),
          };

          const [countRes, teamsRes] = await Promise.all([
            axios.get(`${API_URL}/teams/count`, { params }),
            axios.get(`${API_URL}/teams`, { params }),
          ]);

          const totalTeams = countRes.data.total || 0;
          setTotalPages(Math.max(1, Math.ceil(totalTeams / limit)));
          setTeams(Array.isArray(teamsRes.data) ? teamsRes.data : []);
        }
      } catch (error) {
        console.error("Error fetching teams:", error);
      }
    }, 300),
    [user],
  );

  useEffect(() => {
    fetchTeams(page, searchTerm, statusFilter);
  }, [page, searchTerm, statusFilter, fetchTeams]);

  useEffect(() => {
    axios.get(`${API_URL}/team-levels`).then((res) => setTeamLevels(res.data));
  }, []);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(1);
  };

  const handleClose = () => {
    setShowModal(false);
    setCurrentTeam({
      id: null,
      name: "",
      club: "",
      stadium: "",
      status: 1,
      level_team_id: "",
    });
  };

  const handleShowAdd = () => {
    setIsEditing(false);
    setShowModal(true);
  };

  const handleShowEdit = (team) => {
    setIsEditing(true);
    setCurrentTeam(team);
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setCurrentTeam((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) {
      await axios.put(`${API_URL}/teams/${currentTeam.id}`, currentTeam);
    } else {
      await axios.post(`${API_URL}/teams`, currentTeam);
    }
    fetchTeams(page, searchTerm, statusFilter);
    handleClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm("Bạn có chắc chắn muốn xóa đội này?")) {
      await axios.delete(`${API_URL}/teams/${id}`);
      fetchTeams(page, searchTerm, statusFilter);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {user && user.role === "manager" ? "Đội của tôi" : "Danh sách Đội"}
        </h2>
        {user && user.role === "admin" && (
          <Button variant="primary" onClick={handleShowAdd}>
            Thêm Đội
          </Button>
        )}
      </div>

      {user && user.role === "admin" && (
        <Row className="g-3 mb-3">
          <Col md={8}>
            <Form.Control
              type="text"
              placeholder="Tìm kiếm theo tên đội, CLB, sân..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </Col>
          <Col md={4}>
            <Form.Select value={statusFilter} onChange={handleStatusFilterChange}>
              <option value="all">Tất cả trạng thái</option>
              <option value="1">Hoạt động</option>
              <option value="0">Khóa</option>
            </Form.Select>
          </Col>
        </Row>
      )}

      <div className="table-responsive">
        <table className="table table-striped table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "7%" }}>ID</th>
              <th style={{ width: "23%" }}>Tên</th>
              <th style={{ width: "25%" }}>CLB</th>
              <th style={{ width: "25%" }}>Sân nhà</th>
              <th style={{ width: "10%" }} className="text-center">Trạng thái</th>
              <th style={{ width: "10%" }} className="text-center">Hành động</th>
            </tr>
          </thead>
          <tbody>
            {teams.map((team) => (
              <tr key={team.id}>
                <td>{team.id}</td>
                <td className="text-truncate" style={{ maxWidth: 220 }}>{team.name}</td>
                <td className="text-truncate" style={{ maxWidth: 240 }}>{team.club}</td>
                <td className="text-truncate" style={{ maxWidth: 240 }}>{team.stadium}</td>
                <td className="text-center">{Number(team.status) === 1 ? "Hoạt động" : "Khóa"}</td>
                <td className="text-center text-nowrap">
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => handleShowEdit(team)}
                  >
                    Sửa
                  </Button>{" "}
                  {user && user.role === "admin" && (
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(team.id)}
                      className="ms-1"
                    >
                      Xóa
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {user && user.role === "admin" && (
        <nav>
          <ul className="pagination">
            <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage(page - 1)}>
                Trước
              </button>
            </li>
            <li className="page-item disabled">
              <span className="page-link">
                Trang {page} / {totalPages}
              </span>
            </li>
            <li className={`page-item ${page >= totalPages ? "disabled" : ""}`}>
              <button className="page-link" onClick={() => setPage(page + 1)}>
                Sau
              </button>
            </li>
          </ul>
        </nav>
      )}

      <Modal show={showModal} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? "Sửa Đội" : "Thêm Đội"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Tên Đội</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={currentTeam.name}
                onChange={handleFormChange}
                required
                disabled={user.role === "manager"}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>CLB (Địa chỉ liên hệ)</Form.Label>
              <Form.Control
                type="text"
                name="club"
                value={currentTeam.club}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Sân nhà (Địa chỉ liên hệ)</Form.Label>
              <Form.Control
                type="text"
                name="stadium"
                value={currentTeam.stadium}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Hạng đội</Form.Label>
              <Form.Select
                name="level_team_id"
                value={currentTeam.level_team_id}
                onChange={handleFormChange}
                disabled={user.role === "manager"}
              >
                <option value="">-- Chọn Hạng --</option>
                {teamLevels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Trạng thái</Form.Label>
              <Form.Select
                name="status"
                value={currentTeam.status}
                onChange={handleFormChange}
                disabled={user.role === "manager"}
              >
                <option value="1">Hoạt động</option>
                <option value="0">Khóa</option>
              </Form.Select>
            </Form.Group>
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Hủy
            </Button>
            <Button variant="primary" type="submit">
              Lưu
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default TeamList;
